import type {
  Ticket,
  TicketActionWithSeverity,
  TicketDetails,
  Finding,
} from "@/_store/tickets/tickets.module.ts";
import { FindingDetectedInType } from "@/_store/tickets/tickets.module.ts";
import {
  quickFixStepsMap,
  AllowBlockScope,
  TicketAction,
  TicketActionSeverity,
  TicketFindingType,
  TicketTrigger,
  ticketTriggerIcons,
  TicketType,
  ticketTypeIcons,
  ViolationDirection,
  endpointDataGovernanceSubTriggers,
  userDataGovernanceSubTriggers,
  userDataGovernanceUnifiedTriggers,
  endpointDataGovernanceUnifiedTriggers,
} from "@/constants/tickets.ts";
import union from "lodash/union";
import { EdrProcessStatus } from "@/constants/edr.ts";
import intersection from "lodash/intersection";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import pullAllWith from "lodash/pullAllWith";
import { arrayToLowerCase } from "@coronet/cui";
import escape from "lodash/escape";
import { convertSize, Patterns } from "@coronet/cui";
import { i18n } from "@/plugins/i18n.ts";
import type { ISelection } from "@/_store/selector.module.ts";
import type { DialogConfig } from "@/_store/dialogs.module.ts";
import TicketConfirmationModal from "@/components/modals/TicketConfirmationModal.vue";
import { ModalWidth } from "@/constants/modals.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { useSnackbarStore } from "@/_store/snackbar.module";
import { Service } from "@/constants/cloud-apps";
import { getFormattedDateTime } from "@/_helpers/date.ts";
import { componentDialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";

export interface ContactUserFormData {
  includeEventDetails: boolean;
  contactMessage: string;
  recipients: string[];
}

export interface AllowBlockEmailFormData {
  action: TicketAction;
  closeTicket: boolean;
  description?: string;
  scope: AllowBlockScope;
  metadata: {
    allRecipients: boolean;
    recipients: string[];
  };
}

export interface QuickFixMetadata {
  [TicketAction.CONTACT_USER]?: ContactUserFormData;
  [TicketAction.DISCARD_EMAIL_WITH_OPTIONS]?: AllowBlockEmailFormData;
  selectedActions: TicketAction[];
}

export interface QuickFixPayload<T = TicketDetails> {
  ticket: T;
  metadata: QuickFixMetadata;
}

/**
 * Ticket actions that represent approve email variants.
 */
export const approveEmailActions = [
  TicketAction.APPROVE_EMAILS_FROM_DOMAIN,
  TicketAction.APPROVE_EMAILS_FROM_SENDER,
  TicketAction.APPROVE_IP,
  TicketAction.APPROVE_EMAIL,
];

/**
 * Ticket actions that represent discard email variants.
 */
export const discardEmailActions = [
  TicketAction.DISCARD_EMAILS_FROM_DOMAIN,
  TicketAction.DISCARD_EMAILS_FROM_SENDER,
  TicketAction.DISCARD_IP,
  TicketAction.DISCARD_EMAIL,
];

/**
 * Returns true for ticket types where the affected users count should be displayed.
 * @param ticketType
 */
export const shouldShowUsersNumber = (ticketType: TicketType) => {
  return [TicketType.SIMULATED_EMAIL_PHISHING, TicketType.SECURITY_TRAINING_FAILURE].includes(
    ticketType
  );
};

/**
 * Resolves the list of users to display for a ticket based on its event type and violation direction.
 * @param ticket
 */
export const getTicketDisplayedUsers = (ticket: Ticket) => {
  if (
    [TicketType.EMAIL_PHISHING, TicketType.MALWARE_IN_EMAIL_ATTACHMENTS].includes(ticket.eventType)
  ) {
    return ticket.to ? ticket.to : [];
  }
  if (ticket.lastLoggedInUsers?.length) {
    return ticket.lastLoggedInUsers ?? [];
  }
  const violationDirection = ticket.userDataAccessViolation?.violationDirection;
  if (violationDirection === ViolationDirection.ACCESS) {
    return union(
      ticket.userDataAccessViolation.users,
      ticket.potentiallyProtectableUsers
    ) as string[];
  } else if (violationDirection === ViolationDirection.SHARE) {
    return ticket.userDataAccessViolation.users as string[];
  }
  return ticket.from ? [ticket.from] : [];
};

/**
 * Returns the icon name for a ticket, preferring event type icon and falling back to trigger icon.
 * @param ticket
 */
export const getIconName = ({ eventType, eventTrigger }: Ticket) => {
  return ticketTypeIcons[eventType] || ticketTriggerIcons[eventTrigger];
};

/**
 * Returns true when the ticket's EDR process status is either ALLOWED or BLOCKED.
 * @param item
 */
export const showProcessStatus = (item: Ticket) => {
  return (
    item.processStatus === EdrProcessStatus.ALLOWED ||
    item.processStatus === EdrProcessStatus.BLOCKED
  );
};

/**
 * Returns the subset of potentially protectable users relevant for the ticket's displayed users.
 * @param ticket
 */
export const getPotentiallyProtectableUsers = (ticket: Ticket) => {
  if (
    ticket.eventType === TicketType.EMAIL_PHISHING &&
    ticket.potentiallyProtectableUsers?.length
  ) {
    return ticket.potentiallyProtectableUsers;
  }

  return intersection(
    arrayToLowerCase(ticket.potentiallyProtectableUsers ?? []),
    arrayToLowerCase(getTicketDisplayedUsers(ticket))
  );
};

/**
 * Sorts ticket triggers alphabetically by translated label, always placing EMAIL_PHISHING last.
 * @param triggers
 */
export const sortTicketTriggers = (triggers: TicketTrigger[]) => {
  const sortedTriggers: TicketTrigger[] = triggers
    .filter((e) => e !== TicketTrigger.EMAIL_PHISHING)
    .sort((a, b) =>
      i18n.global
        .t(`tickets.eventTriggers.${a}`)
        .localeCompare(i18n.global.t(`tickets.eventTriggers.${b}`))
    );

  if (triggers.includes(TicketTrigger.EMAIL_PHISHING)) {
    sortedTriggers.push(TicketTrigger.EMAIL_PHISHING);
  }

  return sortedTriggers;
};

export function getTicketConfirmationModalConfig({
  action,
  selectedItems,
  selection,
  selectedItemsCount,
  showCheckbox,
  callback,
  disable,
}: {
  action: TicketAction;
  selectedItems: Ticket[];
  selection: ISelection<Ticket> | Ticket[];
  selectedItemsCount: number;
  showCheckbox: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- callbacks spread `data` and access ticket-action-specific fields; each call site supplies a typed function whose `data` shape differs by action
  callback: (data: any) => Promise<void>;
  disable: boolean;
}): DialogConfig {
  const singleItem = selectedItems.length === 1 ? selectedItems[0] : null;
  let userDataAccessViolationInterpolations = {};
  if (singleItem) {
    userDataAccessViolationInterpolations = {
      violationUsers: (singleItem.userDataAccessViolation?.users ?? []).join(","),
      violationDirection:
        singleItem.userDataAccessViolation?.violationDirection ?? ViolationDirection.SHARE,
      violationTrigger: i18n.global.t(
        `ticketDetails.fullDetails.accessViolations.${singleItem.eventTrigger}`
      ),
    };
  }

  return componentDialogsConfigConstructor({
    item: {
      ...(singleItem ?? {}),
      selection: selection,
      showCheckbox,
      checkboxText: i18n.global.t("general.closeUponConfirmation"),
      text: i18n.global.t(
        `modals.${action}.description`,
        {
          ...userDataAccessViolationInterpolations,
        },
        selectedItemsCount
      ),
    },
    action,
    callback,
    component: TicketConfirmationModal,
    disable,
    width: ModalWidth.SMALL,
  });
}

/**
 * Generic function to apply quick fix actions for tickets
 * @param payload - The quick fix payload containing ticket and metadata
 * @param getQuickFixCallback - Function that returns the appropriate callback for each action
 */
export const applyQuickFixAction = async <T, U>(
  payload: DialogDataConfig<QuickFixPayload<T>>,
  getQuickFixCallback: (
    action: TicketAction,
    payload: DialogDataConfig<QuickFixPayload<T>>
  ) => Promise<U>
) => {
  const snackbarStore = useSnackbarStore();
  try {
    const { selectedActions } = payload.item.metadata!;
    const actionPromises = selectedActions.map((action) => getQuickFixCallback(action, payload));

    await Promise.all(actionPromises);

    const appliedActions = selectedActions
      .map((action) => {
        if (action === TicketAction.DISCARD_EMAIL_WITH_OPTIONS) {
          const discardAction =
            payload.item.metadata[TicketAction.DISCARD_EMAIL_WITH_OPTIONS]?.action;
          return i18n.global.t(`ticketDetails.eventActions.${discardAction}`);
        }
        return i18n.global.t(`ticketDetails.eventActions.${action}`);
      })
      .join(", ");

    snackbarStore.addGenericSuccess(
      i18n.global.t(`snackbar.messages.event.${payload.action}`, { appliedActions })
    );
  } catch (err) {
    console.error(err);
  }
};

/**
 * Builds and sorts the ticket actions list, merging approve/discard email actions
 * into combined actions and handling outbound proxy variants.
 */
export const getTicketActionsList = (
  actions: TicketActionWithSeverity[],
  serviceName?: Service | string
): TicketActionWithSeverity[] => {
  const filteredActions = cloneDeep(actions);
  const approveTicketActions = filteredActions.filter((action) =>
    approveEmailActions.includes(action.name)
  );
  const discardTicketActions = filteredActions.filter((action) =>
    discardEmailActions.includes(action.name)
  );

  if (approveTicketActions.length) {
    if (serviceName === Service.OUTBOUND_PROXY) {
      const approveEmailAction = actions.find(
        (action) => action.name === TicketAction.APPROVE_EMAIL
      );
      filteredActions.push({
        name: TicketAction.APPROVE_OUTBOUND_PROXY_EMAIL,
        severity: approveEmailAction?.severity ?? TicketActionSeverity.MEDIUM,
      });
    } else {
      filteredActions.push({
        name: TicketAction.APPROVE_EMAIL_WITH_OPTIONS,
        severity: approveTicketActions.at(0)?.severity ?? TicketActionSeverity.MEDIUM,
      });
    }
    pullAllWith(filteredActions, approveTicketActions, isEqual);
  }

  if (discardTicketActions.length) {
    if (serviceName === Service.OUTBOUND_PROXY) {
      const discardEmailAction = actions.find(
        (action) => action.name === TicketAction.DISCARD_EMAIL
      );
      filteredActions.push({
        name: TicketAction.DISCARD_OUTBOUND_PROXY_EMAIL,
        severity: discardEmailAction?.severity ?? TicketActionSeverity.MEDIUM,
      });
    } else {
      filteredActions.push({
        name: TicketAction.DISCARD_EMAIL_WITH_OPTIONS,
        severity: discardTicketActions.at(0)?.severity ?? TicketActionSeverity.MEDIUM,
      });
    }
    pullAllWith(filteredActions, discardTicketActions, isEqual);
  }

  return filteredActions;
};

/**
 * Determines if quick fix should be shown by validating there are mapped steps
 * and at least one applicable action from the ticket.
 */
export const ticketQuickFixSectionExists = (details: TicketDetails): boolean => {
  const quickFixActions = quickFixStepsMap[details.eventTrigger];
  if (!quickFixActions?.length) {
    return false;
  }

  const availableActions = getTicketActionsList(details.actions, details.sections?.serviceName).map(
    (action) => action.name
  );
  // Ignore MARK_AS_PROCESSED because it's always present and not a valid quick fix signal.
  const quickFixActionsWithoutClose = quickFixActions.filter(
    (action) => action !== TicketAction.MARK_AS_PROCESSED
  );
  return intersection(availableActions, quickFixActionsWithoutClose).length > 0;
};

export interface SubTriggerItem {
  type: string;
  value: string;
}
/**
 * Computes sub-triggers for ticket filtering based on selected event triggers and widget
 * @param eventTriggers - Array of selected event triggers
 * @returns Array of sub-trigger items with type and value
 */
export const getSubTriggers = (eventTriggers: string[] = []): SubTriggerItem[] => {
  const triggers: SubTriggerItem[] = [];
  const userDataGovernanceSubheader = {
    type: "subheader",
    value: i18n.global.t(`ticketDetails.filters.widgets.userDataGovernance`),
  };

  const endpointDataGovernanceSubheader = {
    type: "subheader",
    value: i18n.global.t(`ticketDetails.filters.widgets.endpointDataGovernance`),
  };

  const hasEmailDlpTrigger = userDataGovernanceUnifiedTriggers.some((trigger) =>
    eventTriggers.includes(trigger)
  );
  const hasDeviceDlpTrigger = endpointDataGovernanceUnifiedTriggers.some((trigger) =>
    eventTriggers.includes(trigger)
  );

  if (hasEmailDlpTrigger) {
    triggers.push(userDataGovernanceSubheader);
    userDataGovernanceSubTriggers.forEach((trigger) => {
      triggers.push({
        type: trigger,
        value: trigger,
      });
    });
  }

  if (hasDeviceDlpTrigger) {
    triggers.push(endpointDataGovernanceSubheader);
    endpointDataGovernanceSubTriggers.forEach((trigger) => {
      triggers.push({
        type: trigger,
        value: trigger,
      });
    });
  }

  return triggers;
};

/**
 * Navigates to a related ticket by adding its event ID as a search parameter
 * to the current URL and forcing a page reload.
 *
 * This is used when viewing related tickets from within the ticket preview,
 * where the route doesn't change but the ticket being displayed does.
 *
 * @param eventId - The event ID of the related ticket to navigate to
 *
 * @example
 * // Navigate to related ticket with ID "abc123"
 * navigateToRelatedTicket("abc123");
 * // Results in: current-url?search=abc123 or current-url&search=abc123
 */
export const navigateToRelatedTicket = (eventId: string): void => {
  let url = window.location.href;

  if (url.indexOf("?") > -1) {
    url += `&search=${eventId}`;
  } else {
    url += `?search=${eventId}`;
  }

  window.location.href = url;
};

/** Returns true when the event type belongs to the device protection domain. */
export const isDeviceTicket = (eventType: TicketType): boolean => {
  return [
    TicketType.MALWARE_IN_DEVICE,
    TicketType.ENDPOINT_VULNERABILITY,
    TicketType.WIFI_PHISHING,
    TicketType.WIFI_FORBIDDEN_NETWORK,
    TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_DEVICE,
  ].includes(eventType);
};

/**
 * Strips HTTP/HTTPS URLs and `<wbr>` line-break hints from raw email HTML.
 * Used before rendering email body content to prevent clickable malicious links.
 */
export const removeMaliciousLinksFromContent = (emailContent: string) => {
  const urlRegex =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/g;

  // Replace <wbr> tags
  emailContent = emailContent.replace(/<wbr>/g, "");

  // Replace URLs
  return emailContent.replace(urlRegex, "");
};

/**
 * Flattens nested `TicketDetails` sections into a single object for use in
 * ticket preview templates. Merges metadata from email, device, network, EDR, and
 * other domain-specific sections onto the top-level ticket shape, and formats
 * `startTime`/`endTime` for display.
 */
export const flattenTicketDetails = (ticketDetails: TicketDetails) => {
  const sections = ticketDetails?.sections;
  const attachmentName =
    sections?.emailMetaData?.attachments?.[0]?.fileName ||
    sections?.emailMetaData?.attachment?.fileName;
  const deviceUser = getDeviceUser();

  function getDeviceUser() {
    const lastLoggedInUsers = ticketDetails?.lastLoggedInUsers ?? [];
    return lastLoggedInUsers.length ? lastLoggedInUsers[0] : ticketDetails?.sections?.triggeredUser;
  }

  const subTriggerKeys = ticketDetails?.sections?.aggregatedEventTriggers ?? [];
  const recipientsList = sections?.emailMetaData?.recipients ?? [];
  const recipients =
    recipientsList.length > 1
      ? `${recipientsList[0]} +${recipientsList.length - 1}`
      : recipientsList[0] || "";

  return {
    ...ticketDetails,
    ...sections?.malwareMetaData,
    ...sections?.emailMetaData,
    ...sections?.deviceMetaData,
    ...sections?.networkMetaData,
    ...sections?.malwareMetaData,
    ...sections?.infectedProccessMetaData,
    ...sections?.systemMetaData?.proxyMisconfiguration,
    ...sections?.edrMetaData,
    ...sections?.edrMetaData?.blockedConnectionMetaData,
    ...sections?.edrMetaData?.asrDetails,
    attachmentName,
    recipients,
    // The malware descriptions already own `{fileName}` via malwareMetaData, so that source
    // keeps precedence and filesMetaData only fills in for the file-integrity ticket.
    fileName: sections?.malwareMetaData?.fileName ?? sections?.filesMetaData?.[0]?.fileName,
    unencryptedDriveName: sections?.deviceMetaData?.unencryptedFileStorage?.name,
    deviceUser: deviceUser,
    triggeredUser: sections.triggeredUser,
    serviceName: i18n.global.t(`services.${ticketDetails.sections.serviceName}`),
    endTime: getFormattedDateTime(ticketDetails.endTime, "lll"),
    startTime: getFormattedDateTime(ticketDetails.startTime, "lll"),
    eventHappened: sections?.counter,
    countryName: sections?.locations[0]?.countryName,
    ip: sections?.locations[0]?.ip,
    subTriggers: subTriggerKeys
      .map((i) => i18n.global.t(`tickets.eventSubTriggers.${i}`))
      .join(", "),
    dlpSharedBy: sections?.filesMetaData?.[0]?.sharedBy ?? "",
    sharedFileName: sections?.filesMetaData?.[0]?.fileName ?? "",
    dlpSharedWith: sections?.filesMetaData?.[0]?.sharedWith?.[0] ?? "",
    userWhoSharedDlp: sections?.userDataAccessViolation?.users?.[0] || "",
    driveName: sections?.deviceMetaData?.scannedFileStorage?.name || "",
    impossibleTravelerEvents: sections?.cloudThreatMetaData?.length || "",
  };
};

/**
 * Cloud Share structure endpoint aggregates findings without a sort stage and the email finding
 * gs are grouped in the order they happen to appear in the payload. Render them in the data governance
 * order instead: the regulatory data types first, keeping their existing order, with
 * the critical data types (Certificates, Source Code, Specific File Types) after.
 * Triggers outside both lists keep their relative order at the end
 */
export const sortFindingsGroupsByDataType = <T extends { eventTrigger: TicketTrigger }>(
  groups: T[]
): T[] => {
  const order = [...userDataGovernanceSubTriggers, ...endpointDataGovernanceSubTriggers];
  const positionOf = (trigger: TicketTrigger) => {
    const index = order.indexOf(trigger);
    return index === -1 ? order.length : index;
  };
  return [...groups].sort((a, b) => positionOf(a.eventTrigger) - positionOf(b.eventTrigger));
};

/**
 * Event-level `sections.subTypes` explains why a sender was blocklisted (IP, country,
 * TLD, etc). Unknown enum values are dropped instead of rendering as a
 * raw translation key.
 */
const getEventSubTypeLabels = (ticket?: TicketDetails): string[] =>
  (ticket?.sections?.subTypes ?? [])
    .filter((subType) => i18n.global.te(`ticketDetails.eventSubTypes.${subType}`))
    .map((subType) => i18n.global.t(`ticketDetails.eventSubTypes.${subType}`));

export const getFindingTypeLabel = (finding: Finding, ticket?: TicketDetails): string => {
  if (finding.type === TicketFindingType.BLOCKLISTED_SENDER) {
    const subTypeLabels = getEventSubTypeLabels(ticket);
    if (subTypeLabels.length) {
      return subTypeLabels.join(", ");
    }
  }
  if (finding.typeUiView) {
    return finding.typeUiView;
  }
  const typeKey = `ticketDetails.incidentFindings.type.${finding.type}`;
  return i18n.global.te(typeKey) ? i18n.global.t(typeKey) : finding.type;
};

/**
 * Builds an HTML string describing a single DLP finding for display in the
 * findings modal. Handles source detection (attachment vs. body), text
 * highlighting within long quotes, BLOCKLISTED_SENDER formatting, and
 * virus-name display. Quotes are HTML-escaped via lodash `escape`.
 */
export const createFindingTemplate = (finding: Finding, ticket: TicketDetails) => {
  const TICKETS_SUPPORTING_FINDINGS_SOURCE = [
    TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_EMAIL,
    TicketType.SUSPECTED_DATA_EXPOSURE_BY_EMAIL,
  ];

  let template = "";
  const quote = escape(finding.quote);
  const longQuote = escape(finding.longQuote);
  const filePath = finding.filePath;
  const virusName = finding.virusName;

  if (TICKETS_SUPPORTING_FINDINGS_SOURCE.includes(ticket?.eventType!)) {
    if (finding.source === FindingDetectedInType.ATTACHMENT) {
      const attachmentsArray = ticket!.sections.emailMetaData.attachments;
      const attachment = attachmentsArray
        ? attachmentsArray.find(({ fileName }) => filePath.includes(fileName))
        : ticket!.sections.emailMetaData.attachment;

      if (attachment?.fileName && filePath.includes(attachment?.fileName)) {
        template += i18n.global.t("modals.dlpFindings.detectedInFile", {
          fileName: attachment!.fileName,
          fileSize: convertSize(attachment!.fileSize, 1),
        });
      }
    } else if (finding.source) {
      template += i18n.global.t("modals.dlpFindings.detectedInGeneral", {
        detectedIn: i18n.global.t(`modals.dlpFindings.findingType.${finding.source}`),
      });
    }
  }

  template += i18n.global.t("modals.dlpFindings.type", {
    type: getFindingTypeLabel(finding, ticket),
  });
  if (longQuote) {
    // if quote present, highlight it, otherwise show longQuote as it is
    if (quote && longQuote.includes(quote)) {
      const highlightedTextStartIndex = longQuote.indexOf(quote);
      const highlightedTextEndIndex = longQuote.indexOf(quote) + quote.length;
      const textBeforeHighlight = longQuote.substring(0, highlightedTextStartIndex);
      const textAfterHighlight = longQuote.substring(highlightedTextEndIndex, longQuote.length);
      const highlightedText = quote;
      template =
        template +
        i18n.global.t("modals.dlpFindings.longQuote", {
          textBeforeHighlight,
          textAfterHighlight,
          highlightedText,
        });
    } else {
      template =
        template +
        i18n.global.t("modals.dlpFindings.longQuoteWithoutHighlight", {
          longQuote,
        });
    }
  }
  if (!longQuote && quote) {
    if (finding.type === TicketFindingType.BLOCKLISTED_SENDER) {
      const quoteParts = quote.split(" ");
      // Legacy backend quotes join the sender email and the sender IP with a space
      // ("user@test.com 1.2.3.4") — list them one by one in a column. New quotes are a
      // single phrase ("Blocklisted domain (test.com)") and are shown as is
      const isSenderWithIp = quoteParts.length === 2 && Patterns.IP.test(quoteParts[1]);
      template += i18n.global.t("modals.dlpFindings.quotePlain", {
        quote: isSenderWithIp ? quoteParts.map((v) => `"${v}"`).join("<br>") : quote,
      });
    } else {
      template += i18n.global.t("modals.dlpFindings.quote", { quote });
    }
  }
  if (virusName) {
    template += i18n.global.t("modals.dlpFindings.virusName", { virusName });
  }
  if (!finding.source && filePath) {
    template += i18n.global.t("modals.dlpFindings.fileName", {
      fileName: filePath,
    });
  }

  return template;
};

/**
 * Sorts an array of ticket actions according to specific business rules:
 * 1. 'Mark as Processed/Unprocessed'
 * 2. All other actions (sorted by severity, critical -> low) and alphabetically by name if they have the same severity
 * 3. 'Contact User'
 *
 * @returns {Array<TicketActionWithSeverity>} A new, sorted array.
 */
export const sortTicketActions = (
  actions: TicketActionWithSeverity[]
): TicketActionWithSeverity[] => {
  const severityOrder = {
    [TicketActionSeverity.CRITICAL]: 3,
    [TicketActionSeverity.MEDIUM]: 2,
    [TicketActionSeverity.LOW]: 1,
  };

  const getSeverity = (action: TicketActionWithSeverity) => severityOrder[action.severity] ?? 0;

  const getSortGroup = (action: TicketActionWithSeverity) => {
    const { name } = action;
    if (name === TicketAction.MARK_AS_PROCESSED || name === TicketAction.MARK_AS_UNPROCESSED) {
      return 1;
    }
    if (name === TicketAction.CONTACT_USER) {
      return 3;
    }
    return 2;
  };

  return actions.slice().sort((a, b) => {
    const groupA = getSortGroup(a);
    const groupB = getSortGroup(b);

    if (groupA !== groupB) {
      return groupA - groupB;
    }

    const severityA = getSeverity(a);
    const severityB = getSeverity(b);

    if (severityA !== severityB) {
      return severityB - severityA;
    }

    return i18n.global
      .t(`tickets.eventTriggers.${a.name}`)
      .localeCompare(i18n.global.t(`tickets.eventTriggers.${b.name}`));
  });
};
